import * as React from "react";
import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

// MUI
import { Box, Typography, Divider, Accordion, AccordionSummary, AccordionDetails } from "@mui/material";

// OpenLayers
import OlCollection from "ol/Collection";
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

// Custom Components
import { BaseLayerSwitcherControl, LayerTree } from "@/components/Map/Controls";

// Types
import { ILayersPane } from "@/@types/components/MapSidebarPanes";
import MapContext from "../Map/MapContext";

const STORAGE_KEY = "base_layer_expansion_states";

const LayersPane: FunctionComponent<ILayersPane> = (props) => {
  const { t } = useTranslation();

  const {
    ready,
    baseLayers,
    apiLayersCollection, clientLayersCollection,
    onLayerVisibilityChange,
    mapId
  } = props;

  const [expanded, setExpanded] = React.useState<boolean>(false);

  // Load from localStorage
  React.useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (typeof parsed === "object" && parsed !== null && mapId in parsed) {
          const value = parsed[mapId];
          if (typeof value === "boolean") {
            setExpanded(value);
            return;
          }
        }
      }
      setExpanded(true);
    } catch (err) {
      console.warn("Failed to parse stored layer visibility settings:", err);
      setExpanded(true);
    }
  }, [mapId]);

  // Save to localStorage
  React.useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      const current: Record<string, boolean> = stored ? JSON.parse(stored) : {};
      if (typeof current === "object" && current !== null) {
        current[mapId] = expanded;
        localStorage.setItem(STORAGE_KEY, JSON.stringify(current));
      }
    } catch (err) {
      console.warn("Failed to save layer visibility to localStorage:", err);
    }
  }, [expanded, mapId]);

  return (
    <Box position="relative" width="100%">
      {baseLayers.length > 0 ? (
        <Accordion elevation={0} expanded={expanded} onChange={(_, isExpanded) => setExpanded(isExpanded)}>
          <AccordionSummary
            expandIcon={<ArrowDownwardIcon />}
            aria-controls="panel1-content"
            id="panel1-header"
            sx={{ pl: 0 }}
          >
            <Typography variant="h6" component="h2">
              {t("titles.base_layer")}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <BaseLayerSwitcherControl mapId={mapId} isMini={false} isPublic layers={baseLayers} />
          </AccordionDetails>
        </Accordion>
      ) : null}
      <Divider variant="middle" sx={{ my: 2 }} />
      <Box>
        <Typography variant="h6" component="h2" mb={2}>
          {t("titles.layers")}
        </Typography>
        <LayerTree
          mapId={mapId}
          ready={!!ready}
          layersCollection={new OlCollection([...clientLayersCollection.getArray(), ...apiLayersCollection.getArray()])}
          onLayerVisibilityChange={onLayerVisibilityChange}
        />
      </Box>
    </Box>
  );
};

export default LayersPane;
